import { makeEntityHook } from "./makeEntityHook";
import {
    challengeActions,
    contactActions,
    userActions,
    userSessionActions,
    needActions,
    propertyActions,
    mandateActions,
    countryActions,
    publicationActions,
    matchingActions,
    transactionActions,
    mediaFileActions,
    taskActions,
    activityActions,
    planningActions,
    birthWishActions,
    documentActions,
    permissionActions,
    leaseActions,
    agentTargetActions
} from "../Redux";

import {
    ChallengeService,
    ContactService,
    UserService,
    UserSessionService,
    NeedService,
    PropertyService,
    MandateService,
    CountryService,
    PublicationService,
    MatchingService,
    TransactionService,
    MediaFileService,
    TaskService,
    ActivityService,
    PlanningService,
    BirthWishService,
    DocumentService,
    PermissionService,
    LeaseService,
    AgentTargetService
} from "../Services";
import { 
    ActivityModel, 
    PublicationModel, 
    BirthWishModel, 
    ChallengeModel, 
    CityModel, 
    ContactModel, 
    CountryModel, 
    DocumentModel, 
    LeaseModel, 
    MandateModel, 
    MatchingModel,
     MediaFileModel, 
     NeedModel, 
     PermissionModel, 
     PlanningModel,  
     PropertyModel, 
     TaskModel, 
     TransactionModel, 
     UserModel, 
     UserSessionModel, 
     AgentTargetModel
} from "@/Domains";
import { DOMAIN_TYPES, POLLER_FREQUENCY } from "@/Shared";


/**
 * =====================================================
 * Entity Hooks Registry
 * =====================================================
 *
 * Centralized registry for domain-specific React hooks.
 *
 * Each exported hook is created using the generic `makeEntityHook` factory.
 * Hooks bind together:
 * - Redux slice selector for the entity state
 * - Corresponding Redux actions
 * - API service for the entity
 *
 * Some hooks are configured with polling to automatically refresh
 * entity data in the background.
 *
 * This registry provides a unified, declarative API for components
 * to interact with domain entities consistently and safely.
 */




/**
 * =====================================================
 * useChallenge
 * =====================================================
 *
 * Custom hook for managing Challenge entities.
 * Binds Redux state, entity actions, and API service.
 */
export const useChallenge = makeEntityHook<ChallengeModel>(
    (state) => state.challenges,
    challengeActions,
    ChallengeService,
    'Challenge'
);


/**
 * =====================================================
 * useContact
 * =====================================================
 *
 * Custom hook for managing Contact entities.
 * Includes polling to keep contact data up to date.
 */
export const useContact = makeEntityHook<ContactModel>(
    (state) => state.contacts,
    contactActions,
    ContactService,
    'Contact',
    { pollerKey: DOMAIN_TYPES.Contact }
);


/**
 * =====================================================
 * useUser
 * =====================================================
 *
 * Custom hook for managing User entities.
 */
export const useUser = makeEntityHook<UserModel>(
    (state) => state.users,
    userActions,
    UserService,
    'User'
);


/**
 * =====================================================
 * useUserSession
 * =====================================================
 *
 * Custom hook for managing UserSession entities.
 */
export const useUserSession = makeEntityHook<UserSessionModel>(
    (state) => state.userSessions,
    userSessionActions,
    UserSessionService,
    'UserSession'
);


/**
 * =====================================================
 * useCountry
 * =====================================================
 *
 * Custom hook for managing Country entities.
 */
export const useCountry = makeEntityHook<CountryModel>(
    (state) => state.countries,
    countryActions,
    CountryService,
    'Country'
);

/**
 * =====================================================
 * usePublication
 * =====================================================
 *
 * Custom hook for managing Advertising Source entities.
 */
export const usePublication = makeEntityHook<PublicationModel>(
    (state) => state.publications,
    publicationActions,
    PublicationService,
    'Publication',
    { 
        pollerKey: DOMAIN_TYPES.Publication, 
        frequency : POLLER_FREQUENCY.MID
    }
);


/**
 * =====================================================
 * useAgentTarget
 * =====================================================
 *
 * Custom hook for managing Advertising Source entities.
 */
export const useAgentTarget = makeEntityHook<AgentTargetModel>(
    (state) => state.agentTargets,
    agentTargetActions,
    AgentTargetService,
    'AgentTarget',
    { 
        pollerKey: DOMAIN_TYPES.Publication, 
        frequency : POLLER_FREQUENCY.MID
    }
);


/**
 * =====================================================
 * useMandate
 * =====================================================
 *
 * Custom hook for managing Mandate entities.
 * Includes polling to keep mandate data up to date.
 */
export const useMandate = makeEntityHook<MandateModel>(
    (state) => state.mandates,
    mandateActions,
    MandateService,
    'Mandate',
    { pollerKey: DOMAIN_TYPES.Mandate }
);

/**
 * =====================================================
 * useMediaFile
 * =====================================================
 *
 * Custom hook for managing MediaFile entities.
 * Includes polling to keep media file data up to date.
 */
export const useMediaFile = makeEntityHook<MediaFileModel>(
    (state) => state.mediaFiles,
    mediaFileActions,
    MediaFileService,
    'MediaFile',
    { pollerKey: 'MediaFile' }
);


/**
 * =====================================================
 * useTask
 * =====================================================
 *
 * Custom hook for managing Task entities.
 */
export const useTask = makeEntityHook<TaskModel>(
    (state) => state.tasks,
    taskActions,
    TaskService,
    'Task',
    { 
        pollerKey: DOMAIN_TYPES.Task, 
        frequency : POLLER_FREQUENCY.LOW
    }
);

/**
 * =====================================================
 * useBirthWish
 * =====================================================
 *
 * Custom hook for managing BirthWish entities.
 */
export const useBirthWish = makeEntityHook<BirthWishModel>(
    (state) => state.birthWishes,
    birthWishActions,
    BirthWishService,
    'BirthWish',
    { 
        pollerKey: DOMAIN_TYPES.BirthWish, 
        frequency : POLLER_FREQUENCY.LOW
    }
);


/**
 * =====================================================
 * useDocument
 * =====================================================
 *
 * Custom hook for managing Document entities.
 * Includes polling to keep document data up to date.
 */
export const useDocument = makeEntityHook<DocumentModel>(
    (state) => state.documents,
    documentActions,
    DocumentService,
    'Document',
    { pollerKey: DOMAIN_TYPES.Document }
);


/**
 * =====================================================
 * usePermission
 * =====================================================
 *
 * Custom hook for managing Permission entities.
 */
export const usePermission = makeEntityHook<PermissionModel>(
    (state) => state.permissions,
    permissionActions,
    PermissionService,
    'Permission'
);


/**
 * =====================================================
 * usePlanning
 * =====================================================
 *
 * Custom hook for managing Planning entities.
 * Includes polling to keep planning data up to date.
 */
export const usePlanning = makeEntityHook<PlanningModel>(
    (state) => state.plannings,
    planningActions,
    PlanningService,
    'Planning',
    { 
        pollerKey: DOMAIN_TYPES.Planning,
        frequency : POLLER_FREQUENCY.MID
    }
);


/**
 * =====================================================
 * useActivity
 * =====================================================
 *
 * Custom hook for managing Activity entities.
 * Includes polling to keep activity data up to date.
 */
export const useActivity = makeEntityHook<ActivityModel>(
    (state) => state.activities,
    activityActions,
    ActivityService,
    'Activity',
    { 
        pollerKey: DOMAIN_TYPES.Activity,
        frequency : POLLER_FREQUENCY.MID
    }
);


/**
 * =====================================================
 * useNeed
 * =====================================================
 *
 * Custom hook for managing Need entities.
 * Includes polling to periodically refresh needs data.
 */
export const useNeed = makeEntityHook<NeedModel>(
    (state) => state.needs,
    needActions,
    NeedService,
    'Need',
    { pollerKey: DOMAIN_TYPES.Need }
);


/**
 * =====================================================
 * useProperty
 * =====================================================
 *
 * Custom hook for managing Property entities.
 * Includes polling to reflect frequent updates.
 */
export const useProperty = makeEntityHook<PropertyModel>(
    (state) => state.properties,
    propertyActions,
    PropertyService,
    'Property',
    { pollerKey: DOMAIN_TYPES.Property }
);


/**
 * =====================================================
 * useMatching
 * =====================================================
 *
 * Custom hook for managing Matching entities.
 * Periodically refreshes matching results.
 */
export const useMatching = makeEntityHook<MatchingModel>(
    (state) => state.matchings,
    matchingActions,
    MatchingService,
    'Matching',
    { pollerKey: DOMAIN_TYPES.Matching }
);


/**
 * =====================================================
 * useTransaction
 * =====================================================
 *
 * Custom hook for managing Transaction entities.
 * Includes polling to track transactional state changes.
 */
export const useTransaction = makeEntityHook<TransactionModel>(
    (state) => state.transactions,
    transactionActions,
    TransactionService,
    'Transaction',
    { pollerKey: DOMAIN_TYPES.Transaction }
);


/**
 * =====================================================
 * useLease
 * =====================================================
 *
 * Custom hook for managing Lease entities.
 * Includes polling to keep lease data synchronized.
 */
export const useLease = makeEntityHook<LeaseModel>(
    (state) => state.leases,
    leaseActions,
    LeaseService,
    'Lease',
    { pollerKey: DOMAIN_TYPES.Lease }
);
